import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Award, 
  Briefcase,
  Dumbbell, 
  Mic,
  Users,
  Video,
  TrendingUp,
  Smile,
  Meh,
  Frown,
  AlertCircle,
  Calendar,
  Zap,
  Target,
  CheckCircle2,
  PlayCircle,
  BarChart3
} from 'lucide-react';
import './OnboardingQuestions.css';

const ACTIVE_GOALS = ['interview', 'content', 'presentation', 'leadership', 'sales'];

export const GOAL_EXPLANATIONS = {
  // This export is kept for legacy/other component usage if needed, 
  // but the component below uses the translated version.
  // Ideally this would be refactored out entirely in favor of i18n keys.
};

export default function OnboardingQuestions({ onComplete }) {
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState('');
  const [persona, setPersona] = useState('');
  const [customProfession, setCustomProfession] = useState('');
  const [confidence, setConfidence] = useState('');
  const [practiceFrequency, setPracticeFrequency] = useState('');
  const [includeEnvironmentFeedback, setIncludeEnvironmentFeedback] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const containerRef = useRef(null);

  // Scroll to top when step changes
  useEffect(() => {
    // Scroll to top of the component or window
    const scrollToTop = () => {
      // Try to scroll the container first (if it exists)
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Fallback to window scroll
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(scrollToTop, 100);
    
    return () => clearTimeout(timeoutId);
  }, [step]);

  const GOALS = useMemo(() => [
    { id: 'interview', icon: Briefcase, label: t('onboarding.goal.interview.label'), description: t('onboarding.goal.interview.description') },
    { id: 'content', icon: Video, label: t('onboarding.goal.content.label'), description: t('onboarding.goal.content.description') },
    { id: 'presentation', icon: Mic, label: t('onboarding.goal.presentation.label'), description: t('onboarding.goal.presentation.description') },
    { id: 'leadership', icon: Award, label: t('onboarding.goal.leadership.label'), description: t('onboarding.goal.leadership.description') },
    { id: 'sales', icon: TrendingUp, label: t('onboarding.goal.sales.label'), description: t('onboarding.goal.sales.description') }
  ], [t]);

  const SCENARIOS = useMemo(() => ({
    interview: {
      question: t('onboarding.scenarios.interview.question'),
      roles: [
        { id: 'tech', label: t('onboarding.scenarios.interview.roles.tech') },
        { id: 'product', label: t('onboarding.scenarios.interview.roles.product') },
        { id: 'marketing', label: t('onboarding.scenarios.interview.roles.marketing') },
        { id: 'sales', label: t('onboarding.scenarios.interview.roles.sales') },
        { id: 'executive', label: t('onboarding.scenarios.interview.roles.executive') },
        { id: 'finance_legal', label: t('onboarding.scenarios.interview.roles.finance_legal') },
        { id: 'hr', label: t('onboarding.scenarios.interview.roles.hr') },
        { id: 'general', label: t('onboarding.scenarios.common.roles.general_professional') }
      ]
    },
    content: {
      question: t('onboarding.scenarios.content.question'),
      roles: [
        { id: 'gaming', label: t('onboarding.scenarios.content.roles.gaming') },
        { id: 'education', label: t('onboarding.scenarios.content.roles.education') },
        { id: 'business', label: t('onboarding.scenarios.content.roles.business') },
        { id: 'lifestyle', label: t('onboarding.scenarios.content.roles.lifestyle') },
        { id: 'fitness', label: t('onboarding.scenarios.content.roles.fitness') },
        { id: 'tech_reviews', label: t('onboarding.scenarios.content.roles.tech_reviews') },
        { id: 'general', label: t('onboarding.scenarios.common.roles.general_professional') }
      ]
    },
    presentation: {
      question: t('onboarding.scenarios.presentation.question'),
      roles: [
        { id: 'investor_pitch', label: t('onboarding.scenarios.presentation.roles.investor_pitch') },
        { id: 'keynote', label: t('onboarding.scenarios.presentation.roles.keynote') },
        { id: 'team_update', label: t('onboarding.scenarios.presentation.roles.team_update') },
        { id: 'webinar', label: t('onboarding.scenarios.presentation.roles.webinar') },
        { id: 'sales_demo', label: t('onboarding.scenarios.presentation.roles.sales_demo') },
        { id: 'academic', label: t('onboarding.scenarios.presentation.roles.academic') },
        { id: 'ceremony', label: t('onboarding.scenarios.presentation.roles.ceremony') },
        { id: 'general', label: t('onboarding.scenarios.common.roles.general_professional') }
      ]
    },
    leadership: {
      question: t('onboarding.scenarios.leadership.question'),
      roles: [
        { id: 'team_motivation', label: t('onboarding.scenarios.leadership.roles.team_motivation') },
        { id: 'crisis', label: t('onboarding.scenarios.leadership.roles.crisis') },
        { id: 'feedback', label: t('onboarding.scenarios.leadership.roles.feedback') },
        { id: 'vision', label: t('onboarding.scenarios.leadership.roles.vision') },
        { id: 'board_meeting', label: t('onboarding.scenarios.leadership.roles.board_meeting') },
        { id: 'mentoring', label: t('onboarding.scenarios.leadership.roles.mentoring') },
        { id: 'delegation', label: t('onboarding.scenarios.leadership.roles.delegation') },
        { id: 'general', label: t('onboarding.scenarios.common.roles.general_leadership') }
      ]
    },
    sales: {
      question: t('onboarding.scenarios.sales.question'),
      roles: [
        { id: 'discovery', label: t('onboarding.scenarios.sales.roles.discovery') },
        { id: 'objections', label: t('onboarding.scenarios.sales.roles.objections') },
        { id: 'retail', label: t('onboarding.scenarios.sales.roles.retail') },
        { id: 'general', label: t('onboarding.scenarios.common.roles.general_sales') }
      ]
    }
  }), [t]);

  const CONFIDENCE_LEVELS = useMemo(() => [
    { 
      id: 'very-high', 
      label: t('onboarding.confidence.very-high.label'), 
      icon: TrendingUp,
      description: t('onboarding.confidence.very-high.description'),
      color: '#10B981'
    },
    { 
      id: 'high', 
      label: t('onboarding.confidence.high.label'), 
      icon: Smile,
      description: t('onboarding.confidence.high.description'),
      color: '#46B5D1'
    },
    { 
      id: 'medium', 
      label: t('onboarding.confidence.medium.label'), 
      icon: Meh,
      description: t('onboarding.confidence.medium.description'),
      color: '#FFC107'
    },
    { 
      id: 'low', 
      label: t('onboarding.confidence.low.label'), 
      icon: Frown,
      description: t('onboarding.confidence.low.description'),
      color: '#FF8C64'
    },
    { 
      id: 'very-low', 
      label: t('onboarding.confidence.very-low.label'), 
      icon: AlertCircle,
      description: t('onboarding.confidence.very-low.description'),
      color: '#EF4444'
    },
  ], [t]);

  const PRACTICE_FREQUENCY = useMemo(() => [
    {
      id: 'casual',
      label: t('onboarding.frequency.casual.label'),
      icon: Calendar,
      frequency: t('onboarding.frequency.casual.frequency'),
      description: t('onboarding.frequency.casual.description')
    },
    {
      id: 'regular',
      label: t('onboarding.frequency.regular.label'),
      icon: Target,
      frequency: t('onboarding.frequency.regular.frequency'),
      description: t('onboarding.frequency.regular.description')
    },
    {
      id: 'intensive',
      label: t('onboarding.frequency.intensive.label'),
      icon: Zap,
      frequency: t('onboarding.frequency.intensive.frequency'),
      description: t('onboarding.frequency.intensive.description')
    }
  ], [t]);

  const customPlaceholdersByGoal = useMemo(() => ({
    interview: t('persona.customPlaceholder_interview'),
    content: t('persona.customPlaceholder_content'),
    presentation: t('persona.customPlaceholder_presentation'),
    leadership: t('persona.customPlaceholder_leadership'),
    sales: t('persona.customPlaceholder_sales'),
  }), [t]);

  const getGoalExplanation = (goalId) => {
    if (!goalId) return null;
    const bullets = t(`onboarding.goal.${goalId}.bullets`, { returnObjects: true });
    
    // Ensure bullets is an array (fallback if key missing or not array)
    const safeBullets = Array.isArray(bullets) ? bullets : [
      t('onboarding.questions.defaultBullet1'),
      t('onboarding.questions.defaultBullet2'),
      t('onboarding.questions.defaultBullet3')
    ];

    return {
      label: t(`onboarding.goal.${goalId}.label`),
      headline: t(`onboarding.goal.${goalId}.headline`),
      bullets: safeBullets,
      timeline: t(`onboarding.goal.${goalId}.timeline`)
    };
  };

  const filteredGoals = useMemo(() => GOALS.filter(g => ACTIVE_GOALS.includes(g.id)), [GOALS]);

  const handleGoalSelect = (goalId) => {
    setGoal(goalId);
  };

  const handleContinue = () => {
    // Prevent any actions if already completed
    if (completed) return;
    
    if (step === 1 && !goal) return;
    if (step === 2 && !persona) return;
    if (step === 2 && persona === 'custom' && !customProfession) return;
    if (step === 3 && !confidence) return;
    if (step === 4 && !practiceFrequency) return;

    // Scroll to top immediately before changing step
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (step === 1) {
      setStep(2);
      return;
    }

    if (step === 2) {
      setStep(3);
      return;
    }

    if (step === 3) {
      setStep(4);
      return;
    }

    if (step === 4) {
      setStep(5);
      return;
    }

    if (step === 5) {
      if (submitting || completed) return;
      setSubmitting(true);
      setError(null);

      const roleLabel = persona === 'custom' 
        ? customProfession 
        : SCENARIOS[goal]?.roles.find(r => r.id === persona)?.label;

      const answers = {
        primaryGoal: goal,
        personaType: persona,
        professionDetails: roleLabel,
        confidenceLevel: confidence,
        practiceCommitment: practiceFrequency,
        commitmentLevel: practiceFrequency,
        includeEnvironmentFeedback: includeEnvironmentFeedback,
        // Set sensible defaults for removed fields
        difficultyBaseline: 'steady',
        coachArchetype: 'default',
        weeklyPromise: 'flexible',
        goalSpecificContext: {
          persona: persona,
          profession: roleLabel
        },
        journeyTemplateId: goal,
        templateId: goal
      };

      const handleCompletion = async () => {
        try {
          const maybePromise = onComplete(answers);
          if (maybePromise && typeof maybePromise.then === 'function') {
            await maybePromise;
          }
          // Mark as completed to prevent any further interactions
          setCompleted(true);
        } catch (error) {
          console.error('Error completing onboarding:', error);
          setError(error.message || t('onboarding.questions.submissionError', 'Failed to complete onboarding. Please try again.'));
          setSubmitting(false);
        }
      };

      handleCompletion();
    }
  };

  const isContinueDisabled =
    completed ||
    submitting ||
    (step === 1 && !goal) ||
    (step === 2 && (!persona || (persona === 'custom' && !customProfession))) ||
    (step === 3 && !confidence) ||
    (step === 4 && !practiceFrequency);

  const totalSteps = 5;
  const buttonLabel = step === 5 
    ? (submitting ? t('onboarding.questions.gettingStarted') : t('onboarding.questions.getStarted')) 
    : t('onboarding.questions.continue');

  const getStepContext = () => {
    switch(step) {
      case 1:
        return {
          explanation: t('onboarding.questions.step1ContextExplanation'),
          preview: goal
            ? t('onboarding.questions.step1ContextPreviewWithGoal', {
                goalLabel: GOALS.find(g => g.id === goal)?.label || t('onboarding.questions.defaultGoalLabel')
              })
            : t('onboarding.questions.step1ContextPreviewDefault')
        };
      case 2:
        return {
          explanation: goal ? t('onboarding.questions.step2LabelDynamic', {
             question: SCENARIOS[goal]?.question || t('onboarding.questions.step2LabelDefault')
          }) : t('persona.subtitle'),
          preview: persona ? t('onboarding.questions.step1ContextPreviewWithGoal', {
            goalLabel: persona === 'custom' ? customProfession : SCENARIOS[goal]?.roles.find(p => p.id === persona)?.label
          }) : null
        };
      case 3:
        return {
          explanation: t('onboarding.questions.step2ContextExplanation'),
          preview: confidence ? t('onboarding.questions.step2ContextPreview', {
            confidenceLabel: CONFIDENCE_LEVELS.find(c => c.id === confidence)?.label.toLowerCase() || t('onboarding.questions.defaultConfidenceLabel')
          }) : null
        };
      case 4:
        return {
          explanation: t('onboarding.questions.step3ContextExplanation'),
          preview: practiceFrequency ? t('onboarding.questions.step3ContextPreview', {
            frequencyLabel: PRACTICE_FREQUENCY.find(p => p.id === practiceFrequency)?.label.toLowerCase() || t('onboarding.questions.defaultFrequencyLabel')
          }) : null
        };
      case 5:
        return {
          explanation: t('onboarding.questions.step4ContextExplanation'),
          preview: null
        };
      default:
        return { explanation: "", preview: null };
    }
  };

  const stepContext = getStepContext();
  const selectedGoalExplanation = getGoalExplanation(goal);

  // Show completion/loading state after submission
  if (completed || (submitting && step === 5)) {
    return (
      <div className="onboardingQuestions" ref={containerRef}>
        <div className="onboardingQuestions__progressBar">
          <div 
            className="onboardingQuestions__progressFill"
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '3rem 2rem',
          textAlign: 'center',
          gap: '1.5rem'
        }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <div>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: 600 }}>
              {t('onboarding.questions.completing', 'Completing your setup...')}
            </h3>
            <p style={{ margin: 0, color: '#666', fontSize: '0.95rem' }}>
              {t('onboarding.questions.redirecting', 'Redirecting to your dashboard')}
            </p>
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="onboardingQuestions" ref={containerRef}>
      {/* Progress Bar */}
      <div className="onboardingQuestions__progressBar">
        <div 
          className="onboardingQuestions__progressFill"
          style={{ width: `${(step / totalSteps) * 100}%` }}
        />
      </div>

      {/* Context Explanation */}
      {stepContext.explanation && (
        <div className="onboardingQuestions__context">
          <p className="onboardingQuestions__contextText">{stepContext.explanation}</p>
          {stepContext.preview && (
            <p className="onboardingQuestions__preview">{stepContext.preview}</p>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="onboardingQuestions__step">
          <label className="onboardingQuestions__label">
            {t('onboarding.questions.step1Label')} <span className="required">{t('onboarding.questions.step1Required')}</span>
          </label>
          <div className="onboardingQuestions__goalLayout">
            <div className="onboardingQuestions__goalList">
              <div className="onboardingQuestions__goalListHeader">
                <span className="onboardingQuestions__goalListTitle">
                  {t('onboarding.questions.chooseFocusTitle')}
                </span>
                <span className="onboardingQuestions__goalListHint">
                  {t('onboarding.questions.chooseFocusHint')}
                </span>
              </div>
          <div className="onboardingQuestions__options">
            {filteredGoals.map((goalOption) => {
              const IconComponent = goalOption.icon;
              return (
                <button
                  key={goalOption.id}
                  type="button"
                  className={`onboardingQuestions__option ${goal === goalOption.id ? 'active' : ''}`}
                  onClick={() => handleGoalSelect(goalOption.id)}
                  disabled={submitting || completed}
                >
                  <div className="onboardingQuestions__icon">
                    <IconComponent size={32} />
                  </div>
                  <div className="onboardingQuestions__optionContent">
                    <span className="onboardingQuestions__optionLabel">
                      {goalOption.label}
                    </span>
                    <span className="onboardingQuestions__optionDescription">
                      {goalOption.description}
                    </span>
                  </div>
                </button>
              );
            })}
              </div>
            </div>

            <div className="onboardingQuestions__goalDetails">
              <div className="onboardingQuestions__goalDetailsHeader">
                <span className="onboardingQuestions__goalDetailsBadge">
                  <PlayCircle size={14} />
                  <span>{t('onboarding.questions.learnByDoingBadge')}</span>
                </span>
                <h3 className="onboardingQuestions__goalDetailsTitle">
                  {selectedGoalExplanation
                    ? t('onboarding.questions.goalDetailsTitleWithLabel', { label: selectedGoalExplanation.label })
                    : t('onboarding.questions.goalDetailsDefaultTitle')}
                </h3>
                <p className="onboardingQuestions__goalDetailsSubtitle">
                  {selectedGoalExplanation
                    ? selectedGoalExplanation.headline
                    : t('onboarding.questions.goalDetailsDefaultSubtitle')}
                </p>
              </div>

              <ul className="onboardingQuestions__goalDetailsList">
                {(selectedGoalExplanation?.bullets || [
                  t('onboarding.questions.defaultBullet1'),
                  t('onboarding.questions.defaultBullet2'),
                  t('onboarding.questions.defaultBullet3')
                ]).map((item, index) => (
                  <li key={index} className="onboardingQuestions__goalDetailsItem">
                    <span className="onboardingQuestions__goalDetailsIcon">
                      {index === 0 && <CheckCircle2 size={16} />}
                      {index === 1 && <PlayCircle size={16} />}
                      {index === 2 && <BarChart3 size={16} />}
                      {index > 2 && <CheckCircle2 size={16} />}
                    </span>
                    <span className="onboardingQuestions__goalDetailsText">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="onboardingQuestions__goalDetailsFooter">
                <span className="onboardingQuestions__goalDetailsTimeline">
                  {selectedGoalExplanation?.timeline ||
                    t('onboarding.questions.goalDetailsTimelineFallback')}
                </span>
                <span className="onboardingQuestions__goalDetailsNote">
                  {t('onboarding.questions.goalDetailsNote')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="onboardingQuestions__step">
          <label className="onboardingQuestions__label">
             {SCENARIOS[goal]?.question || t('persona.label')} <span className="required">*</span>
          </label>
          <div className="onboardingQuestions__options">
            {goal && SCENARIOS[goal]?.roles.map((p) => {
              // Icons are less critical here as we have many roles, we can use a generic check or just text
              // For now, let's keep it simple with text cards or generic icons if we want
               const isGeneral = p.id === 'general';
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`onboardingQuestions__option ${persona === p.id ? 'active' : ''} ${isGeneral ? 'onboardingQuestions__option--general' : ''}`}
                  onClick={() => setPersona(p.id)}
                  disabled={submitting || completed}
                >
                  <div className="onboardingQuestions__optionContent">
                    <span className="onboardingQuestions__optionLabel">
                      {p.label}
                    </span>
                  </div>
                   {persona === p.id && <div className="onboardingQuestions__checkIcon"><CheckCircle2 size={20}/></div>}
                </button>
              );
            })}
          </div>
          
           {/* Allow custom input always as an "Other" option if needed, or if they pick 'custom' (though we removed explicit custom button, we can add it back to the list if desired) */}
           <div className="onboardingQuestions__customFieldWrapper">
              <label className="onboardingQuestions__label" style={{ fontSize: '0.9rem', marginTop: '1rem' }}>
                {t('persona.customLabelOptional')}
              </label>
               <input
                type="text"
                className="onboardingQuestions__input"
                placeholder={customPlaceholdersByGoal[goal] || t('persona.customPlaceholder')}
                value={customProfession}
                onChange={(e) => {
                    setCustomProfession(e.target.value);
                    if(e.target.value) setPersona('custom');
                }}
                disabled={submitting || completed}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  fontSize: '1rem',
                  marginTop: '0.5rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  opacity: (submitting || completed) ? 0.6 : 1,
                  cursor: (submitting || completed) ? 'not-allowed' : 'text'
                }}
              />
           </div>
        </div>
      )}

      {step === 3 && (
        <div className="onboardingQuestions__step">
          <label className="onboardingQuestions__label">
            {t('onboarding.questions.step2Label')}
            <span className="required"> *</span>
          </label>
          <div className="onboardingQuestions__confidenceOptions">
            {CONFIDENCE_LEVELS.map((level) => {
              const IconComponent = level.icon;
              return (
                <button
                  key={level.id}
                  type="button"
                  className={`onboardingQuestions__confidenceOption ${confidence === level.id ? 'active' : ''}`}
                  onClick={() => setConfidence(level.id)}
                  disabled={submitting || completed}
                  style={confidence === level.id ? { '--confidence-color': level.color } : {}}
                >
                  <div className="onboardingQuestions__confidenceIcon">
                    <IconComponent size={24} />
                  </div>
                  <div className="onboardingQuestions__confidenceContent">
                    <span className="onboardingQuestions__confidenceLabel">
                      {level.label}
                    </span>
                    <span className="onboardingQuestions__confidenceDescription">
                      {level.description}
                    </span>
                  </div>
                  {confidence === level.id && (
                    <div className="onboardingQuestions__confidenceCheck">
                      <div className="onboardingQuestions__confidenceCheckmark"></div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="onboardingQuestions__step">
          <label className="onboardingQuestions__label">
            {t('onboarding.questions.step3Label')}
            <span className="required"> *</span>
          </label>
          <div className="onboardingQuestions__options">
            {PRACTICE_FREQUENCY.map((freq) => {
              const IconComponent = freq.icon;
              return (
                <button
                  key={freq.id}
                  type="button"
                  className={`onboardingQuestions__option ${practiceFrequency === freq.id ? 'active' : ''}`}
                  onClick={() => setPracticeFrequency(freq.id)}
                  disabled={submitting || completed}
                >
                  <div className="onboardingQuestions__icon">
                    <IconComponent size={32} />
                  </div>
                  <div className="onboardingQuestions__optionContent">
                    <span className="onboardingQuestions__optionLabel">
                      {freq.label}
                    </span>
                    <span className="onboardingQuestions__optionDescription">
                      <strong>{freq.frequency}</strong> —{' '}
                      {freq.description}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="onboardingQuestions__step">
          <label className="onboardingQuestions__label">
            {t('onboarding.questions.step4Title')}
          </label>
          <p className="onboardingQuestions__description">
            {t('onboarding.questions.step4Description')}
          </p>
          <div className="onboardingQuestions__options">
            <button
              type="button"
              className={`onboardingQuestions__option ${includeEnvironmentFeedback === true ? 'active' : ''}`}
              onClick={() => setIncludeEnvironmentFeedback(true)}
              disabled={submitting || completed}
            >
              <div className="onboardingQuestions__icon">
                <Video size={32} />
              </div>
              <div className="onboardingQuestions__optionContent">
                <span className="onboardingQuestions__optionLabel">
                  {t('onboarding.questions.environmentFeedbackOption1')}
                </span>
                <span className="onboardingQuestions__optionDescription">
                  {t('onboarding.questions.environmentFeedbackOption1Desc')}
                </span>
              </div>
            </button>
            <button
              type="button"
              className={`onboardingQuestions__option ${includeEnvironmentFeedback === false ? 'active' : ''}`}
              onClick={() => setIncludeEnvironmentFeedback(false)}
              disabled={submitting || completed}
            >
              <div className="onboardingQuestions__icon">
                <Target size={32} />
              </div>
              <div className="onboardingQuestions__optionContent">
                <span className="onboardingQuestions__optionLabel">
                  {t('onboarding.questions.environmentFeedbackOption2')}
                </span>
                <span className="onboardingQuestions__optionDescription">
                  {t('onboarding.questions.environmentFeedbackOption2Desc')}
                </span>
              </div>
            </button>
          </div>
          <p className="onboardingQuestions__note">
            {t('onboarding.questions.environmentFeedbackNote')}
          </p>
          {error && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem 1rem',
              backgroundColor: '#FEE2E2',
              border: '1px solid #FCA5A5',
              borderRadius: '8px',
              color: '#DC2626',
              fontSize: '0.9rem'
            }}>
              {error}
            </div>
          )}
        </div>
      )}

      <div className="onboardingQuestions__actions">
        <span className="onboardingQuestions__progress">
          {t('onboarding.questions.stepsProgress', { step, total: totalSteps })}
        </span>
        {step > 1 && !submitting && !completed && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              // Scroll to top before going back
              if (containerRef.current) {
                containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
              } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
              setStep(Math.max(1, step - 1));
            }}
            disabled={submitting || completed}
          >
            {t('onboarding.questions.back')}
          </button>
        )}
        <button
          type="button"
          className="btn btn--primary"
          onClick={handleContinue}
          disabled={isContinueDisabled || submitting}
        >
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}
